import { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  HiOutlineArchiveBox,
  HiOutlineArrowPath,
  HiOutlineEye,
  HiOutlineFolder,
  HiOutlineMagnifyingGlass,
  HiOutlinePencilSquare,
  HiOutlinePlus,
} from "react-icons/hi2";

import { useNavigate } from "react-router-dom";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";
import PageCard from "../../../components/ui/pageCard/PageCard";
import Modal from "../../../components/ui/modal/Modal";
import ConfirmModal from "../../../components/ui/confirmModal/ConfirmModal";

import DossierForm from "../../../components/forms/dossier/DossierForm";
import DossierEditModal from "../../../components/dossiers/DossierEditModal";

import usePermissions from "../../../hooks/usePermissions";

import {
  archiveDossier,
  getDossiers,
  restoreDossier,
  setDossierFilters,
  setDossierPage,
} from "../../../redux/features/dossiers/dossierSlice";

import { getUsers } from "../../../redux/features/users/userSlice";

import "./DossiersPage.css";

const STEP_LABELS = {
  aanmelding: "Aanmelding",
  administratie: "Administratie",
  begeleiding: "Begeleiding",
  vdab: "VDAB",
  kld: "KLD",
  afronding: "Afronding",
};

const STATUS_LABELS = {
  active: "Actief",
  paused: "Gepauzeerd",
  completed: "Afgerond",
  cancelled: "Geannuleerd",
};

export default function DossiersPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const { dossiers, filters, pagination, isLoading, isUpdating, error } =
    useSelector((state) => state.dossiers);

  const { users = [] } = useSelector((state) => state.users || {});

  const [searchValue, setSearchValue] = useState(filters.search || "");
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedEditDossier, setSelectedEditDossier] = useState(null);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Bevestigen",
    variant: "danger",
    onConfirm: null,
  });

  const coaches = useMemo(() => {
    return users.filter((user) =>
      ["POWER_INTERNAL_COACH", "POWER_EXTERNAL_COACH"].includes(user.role),
    );
  }, [users]);

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(
        setDossierFilters({
          search: searchValue,
        }),
      );
    }, 400);

    return () => clearTimeout(timer);
  }, [dispatch, searchValue]);

  useEffect(() => {
    dispatch(
      getDossiers({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      }),
    ).finally(() => {
      setHasLoadedOnce(true);
    });
  }, [dispatch, pagination.page, pagination.limit, filters]);

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      title: "",
      message: "",
      confirmText: "Bevestigen",
      variant: "danger",
      onConfirm: null,
    });
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    if (name === "search") {
      setSearchValue(value);
      return;
    }

    dispatch(
      setDossierFilters({
        [name]: value,
      }),
    );
  };

  const openEdit = (dossier) => {
    setSelectedEditDossier(dossier);
    setOpenEditModal(true);
  };

  const getClientName = (dossier) => {
    return (
      dossier.client?.display_name ||
      `${dossier.client?.first_name || ""} ${dossier.client?.last_name || ""}`.trim() ||
      "Onbekende cliënt"
    );
  };

  const openArchiveConfirm = (dossier) => {
    setConfirmModal({
      isOpen: true,
      title: "Dossier archiveren?",
      message: `${getClientName(dossier)} wordt gearchiveerd.`,
      confirmText: "Archiveren",
      variant: "danger",
      onConfirm: async () => {
        await dispatch(archiveDossier(dossier._id)).unwrap();
        closeConfirmModal();
      },
    });
  };

  const openRestoreConfirm = (dossier) => {
    setConfirmModal({
      isOpen: true,
      title: "Dossier herstellen?",
      message: `${getClientName(dossier)} wordt opnieuw actief.`,
      confirmText: "Herstellen",
      variant: "success",
      onConfirm: async () => {
        await dispatch(restoreDossier(dossier._id)).unwrap();
        closeConfirmModal();
      },
    });
  };

  if (isLoading && !hasLoadedOnce) {
    return <PageLoader title="Dossiers laden..." />;
  }

  return (
    <div className="dossiers-page">
      <PageHeader
        eyebrow="POWER"
        title="Dossiers"
        description="Overzicht van alle dossiers."
        actions={
          can("dossier.create") && (
            <button
              className="dossiers-page__create-button"
              onClick={() => setOpenCreateModal(true)}
            >
              <HiOutlinePlus />
              <span>Nieuw dossier</span>
            </button>
          )
        }
      />

      {error && <div className="dossiers-page__error">{error}</div>}

      <PageCard className="dossiers-page__filters">
        <div className="dossiers-page__filters-grid">
          <div className="dossiers-page__search">
            <HiOutlineMagnifyingGlass />

            <input
              name="search"
              value={searchValue}
              onChange={handleFilterChange}
              placeholder="Zoeken op cliënt..."
              autoComplete="off"
            />
          </div>

          <select
            name="status"
            value={filters.status || ""}
            onChange={handleFilterChange}
          >
            <option value="">Alle statussen</option>
            <option value="active">Actief</option>
            <option value="paused">Gepauzeerd</option>
            <option value="completed">Afgerond</option>
            <option value="cancelled">Geannuleerd</option>
          </select>

          <select
            name="current_step"
            value={filters.current_step || ""}
            onChange={handleFilterChange}
          >
            <option value="">Alle stappen</option>
            <option value="aanmelding">Aanmelding</option>
            <option value="administratie">Administratie</option>
            <option value="begeleiding">Begeleiding</option>
            <option value="vdab">VDAB</option>
            <option value="kld">KLD</option>
            <option value="afronding">Afronding</option>
          </select>
        </div>
      </PageCard>

      {isLoading && hasLoadedOnce && (
        <div className="dossiers-page__small-loading">
          Dossiers bijwerken...
        </div>
      )}

      {dossiers.length === 0 ? (
        <PageEmpty
          icon={<HiOutlineFolder />}
          title="Geen dossiers gevonden"
          description="Er zijn momenteel geen dossiers beschikbaar."
        />
      ) : (
        <>
          <div className="dossiers-page__grid">
            {dossiers.map((dossier) => (
              <PageCard key={dossier._id} className="dossiers-page__card">
                <div className="dossiers-page__card-top">
                  <div>
                    <h3>{getClientName(dossier)}</h3>

                    <span>#{dossier._id}</span>
                  </div>

                  <div
                    className={`dossiers-page__status dossiers-page__status--${dossier.status}`}
                  >
                    {STATUS_LABELS[dossier.status] ||
                      dossier.status ||
                      "Onbekend"}
                  </div>
                </div>

                <div className="dossiers-page__meta">
                  <div>
                    <strong>Coach</strong>

                    <span>
                      {dossier.coach
                        ? `${dossier.coach.first_name} ${dossier.coach.last_name}`
                        : "-"}
                    </span>
                  </div>

                  <div>
                    <strong>Stap</strong>

                    <span>
                      {STEP_LABELS[dossier.current_step] ||
                        dossier.current_step ||
                        "-"}
                    </span>
                  </div>

                  <div>
                    <strong>Schooljaar</strong>

                    <span>{dossier.school_year?.name || "-"}</span>
                  </div>

                  <div>
                    <strong>Laatst gewijzigd</strong>

                    <span>
                      {dossier.updatedAt
                        ? new Date(dossier.updatedAt).toLocaleDateString(
                            "nl-BE",
                          )
                        : "-"}
                    </span>
                  </div>
                </div>

                <div className="dossiers-page__actions">
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/dashboard/dossiers/${dossier._id}`)
                    }
                    title="Open dossier"
                  >
                    <HiOutlineEye />
                    <span>Open</span>
                  </button>

                  {can("dossier.update") && (
                    <button
                      type="button"
                      onClick={() => openEdit(dossier)}
                      title="Wijzigen"
                    >
                      <HiOutlinePencilSquare />
                      <span>Wijzig</span>
                    </button>
                  )}

                  {can("dossier.update") && !dossier.is_archived && (
                    <button
                      type="button"
                      className="dossiers-page__danger-action"
                      onClick={() => openArchiveConfirm(dossier)}
                      disabled={isUpdating}
                      title="Archiveren"
                    >
                      <HiOutlineArchiveBox />
                      <span>Archiveer</span>
                    </button>
                  )}

                  {can("dossier.update") && dossier.is_archived && (
                    <button
                      type="button"
                      className="dossiers-page__success-action"
                      onClick={() => openRestoreConfirm(dossier)}
                      disabled={isUpdating}
                      title="Herstellen"
                    >
                      <HiOutlineArrowPath />
                      <span>Herstel</span>
                    </button>
                  )}
                </div>
              </PageCard>
            ))}
          </div>

          {pagination.pages > 1 && (
            <div className="dossiers-page__pagination">
              <button
                disabled={pagination.page <= 1}
                onClick={() => dispatch(setDossierPage(pagination.page - 1))}
              >
                Vorige
              </button>

              <span>
                Pagina {pagination.page} van {pagination.pages} ·{" "}
                {pagination.total} dossiers
              </span>

              <button
                disabled={pagination.page >= pagination.pages}
                onClick={() => dispatch(setDossierPage(pagination.page + 1))}
              >
                Volgende
              </button>
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        title="Nieuw dossier"
        description="Maak een nieuw dossier aan en wijs het toe aan een coach."
      >
        <DossierForm
          onSuccess={() => {
            setOpenCreateModal(false);

            dispatch(
              getDossiers({
                page: pagination.page,
                limit: pagination.limit,
                ...filters,
              }),
            );
          }}
          onCancel={() => setOpenCreateModal(false)}
        />
      </Modal>

      <DossierEditModal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        dossier={selectedEditDossier}
        coaches={coaches}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
      />
    </div>
  );
}
